import React, { MouseEventHandler, useState } from "react";
import { signUpFormValidators } from "../../../../helpers/signUpFormConstants";
import FloatingLabelInput from "../../Inputs/FloatingLabelInput";
import InputWrapper from "../../Inputs/InputWrapper";
import { Controller, useFormContext } from "react-hook-form";
import { useSignUpForm } from "../../SignUpFormContext";
import {
  initialEmailSuggestion,
  emailTakenErrorMsg,
  mailCheck,
  initialEmailErrorMsg,
} from "./emailHelpers";
import * as Form from "@radix-ui/react-form";
import { SignUpFormData } from "../../../../SignUpFormTypes/signUpFormTypes";
import {
  detectAutofill,
  removeDataAttributesOfExtensions,
} from "../../signUpHelpers";

const EmailInput: React.FC = () => {
  const { setValue, control, setError } = useFormContext<SignUpFormData>();
  const { dbEmailExists } = useSignUpForm();

  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState(
    initialEmailSuggestion
  );
  const [wasFocused, setWasFocused] = useState(false);

  const INPUT_NAME = "email";

  const verifyEmail = async (value: string) => {
    setEmailSuggestion(initialEmailSuggestion);

    if (!value.match(signUpFormValidators.email.pattern.value)) {
      return initialEmailErrorMsg;
    }

    setCheckingEmail(true);
    const emailExists = await dbEmailExists(value);
    setCheckingEmail(false);

    if (emailExists) {
      return setEmailExistsMessage();
    }

    mailCheck(
      value,
      (emailSuggestion: Record<string, any>) => {
        setEmailSuggestion({
          shouldShow: true,
          suggestion: emailSuggestion.full,
        });
      },
      () => {
        setEmailSuggestion(initialEmailSuggestion);
      }
    );

    return true;

    function setEmailExistsMessage() {
      const element = document.getElementById(INPUT_NAME);
      const wasAutoFilled = detectAutofill(element, value);
      let shouldAddSpace = "";
      if (wasAutoFilled) shouldAddSpace = " ";
      setValue(INPUT_NAME, value + shouldAddSpace, {
        shouldTouch: true,
        shouldDirty: true,
      });
      setError(INPUT_NAME, { message: emailTakenErrorMsg });
      removeDataAttributesOfExtensions(element);
      setWasFocused(true);
      setEmailSuggestion(initialEmailSuggestion);
      return emailTakenErrorMsg;
    }
  };

  const onSuggestionClick: MouseEventHandler<HTMLSpanElement> = e => {
    setValue(INPUT_NAME, e.currentTarget.innerText, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    setWasFocused(true);
    setEmailSuggestion(initialEmailSuggestion);
  };

  return (
    <Controller
      name={INPUT_NAME}
      rules={{ ...signUpFormValidators.email, validate: verifyEmail }}
      control={control}
      render={({
        field: { onBlur, ...field },
        fieldState: { invalid, error, isDirty },
      }) => (
        <InputWrapper
          label="Your email"
          name={INPUT_NAME}
          valueExists={!!field.value}
          loading={checkingEmail}
          valid={!invalid && isDirty}
        >
          <FloatingLabelInput
            hasError={invalid && wasFocused}
            autoComplete={INPUT_NAME}
            id={INPUT_NAME}
            type="email"
            onBlur={() => setWasFocused(true)}
            {...field}
          />
          {emailSuggestion.shouldShow && !invalid && (
            <Form.Message className="text-forest-600 font-normal text-base leading-160 not-italic leading-[160%]">
              Did you mean{" "}
              <span
                className="text-forest-900 font-normal cursor-pointer underline"
                onClick={onSuggestionClick}
              >
                {emailSuggestion.suggestion}
              </span>
            </Form.Message>
          )}

          {invalid && wasFocused && (
            <p
              className="text-clay-700 text-[16px] font-normal not-italic leading-[160%]"
              dangerouslySetInnerHTML={{ __html: error?.message ?? "" }}
            />
          )}
        </InputWrapper>
      )}
    />
  );
};

export default EmailInput;
